import type { FC, PropsWithChildren } from 'react'
import { useRef } from 'react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'

import Media from '@segment/matcha/components/Media'
import SelectBase from '@segment/matcha/components/Select'
import useSelect from '@segment/matcha/components/Select/useSelect'
import Input from '@segment/matcha/components/Input'
import { ChevronDownIcon, ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import type { Option } from '@segment/matcha/components/Menu/useMenu'
import ButtonText from '@segment/matcha/components/ButtonText'

import TableOfContentsBase, * as TableOfContents from '@segment/matcha/recipes/TableOfContents'

import useMaskOnScroll from '@segment/utils/useMaskOnScroll'

import { scrollToAnchor } from '@segment/utils/useScrollToAnchor'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Section`,
}

const button = {
  text: `Request a demo`,
  url: `/demo/`,
}

interface SidebarProps {
  id: string
  data: Option[]
}

const Sidebar: FC<PropsWithChildren<SidebarProps>> = ({ id, data }) => {
  return (
    <>
      <Media greaterThanOrEqual="xl">
        <Toc data={data} />
      </Media>
      <Media lessThan="xl">
        <MobileSelect id={id} data={data} />
      </Media>
    </>
  )
}

interface TocProps {
  data: SidebarProps[`data`]
}
const Toc: FC<PropsWithChildren<TocProps>> = ({ data }) => {
  const scrollableContainerRef = useRef<HTMLDivElement>(null)
  const pageConfig = usePageConfig()

  const maskOnScroll = useMaskOnScroll({
    wrapper: scrollableContainerRef,
    axis: `vertical`,
    offset: `200px`,
  })

  return (
    <StickyToC>
      <TableOfContents.Title>{copy.heading}</TableOfContents.Title>
      <TocInner>
        {data && (
          <Mask style={{ WebkitMaskImage: maskOnScroll, maskImage: maskOnScroll }}>
            <ToCOverflow ref={scrollableContainerRef} tabIndex={0}>
              <TableOfContents.List>
                {data.map((item) => (
                  <TableOfContentsItem key={item.value} slug={item.value} text={item.text} />
                ))}
              </TableOfContents.List>
            </ToCOverflow>
          </Mask>
        )}
        <ButtonText tag="a" href={getAbsoluteUrl(button.url, pageConfig)} iconEnd={ChevronRightIcon}>
          {button.text}
        </ButtonText>
      </TocInner>
    </StickyToC>
  )
}

const StickyToC = styled(TableOfContentsBase)`
  top: 160px;
  left: 0;
  position: sticky;
`

const TocInner = styled.div`
  display: grid;
  gap: 40px;
  justify-items: start;
`

const ToCOverflow = styled.div`
  overflow-y: auto;
  max-height: calc(100vh - 300px);
`

const Mask = styled(m.div)``

type MobileSelectProps = {
  id: SidebarProps[`id`]
  data: SidebarProps[`data`]
}

const MobileSelect: FC<PropsWithChildren<MobileSelectProps>> = ({ id, data }) => {
  const { state, getControlProps, filteredOptions, getSelectProps } = useSelect({
    options: data,
    id,
    onSelect(e) {
      scrollToAnchor(e[0]?.value, -100)
    },
    config: {
      mobileConfirmationBehaviour: false,
    },
    label: `Select a section`,
  })

  return (
    <>
      <Input
        name={id}
        rightSecondIcon={<ChevronDownIcon />}
        {...getControlProps({
          placeholder: copy.heading,
        })}
      />
      <SelectBase
        id={`select-base-${id}`}
        name={`select-base-${id}`}
        options={data}
        filteredOptions={filteredOptions}
        displayMode={state.displayMode}
        open={state.open}
        {...getSelectProps()}
      />
    </>
  )
}

type TableOfContentsItemProps = {
  slug: string
  text: string
}

const TableOfContentsItem: FC<PropsWithChildren<TableOfContentsItemProps>> = ({ slug, text }) => {
  return (
    <TableOfContents.ListItem
      borderDirection="bottom"
      href={`#${slug}`}
      onClick={(e) => {
        e.preventDefault()
        scrollToAnchor(slug, -100)
      }}
    >
      <TableOfContents.LeftContent>
        <TableOfContents.Text variant="pMedium">{text}</TableOfContents.Text>
      </TableOfContents.LeftContent>
    </TableOfContents.ListItem>
  )
}

export default Sidebar
